import { useState } from 'react';
import { api } from '../api.js';
import { AppIcon } from '../components.jsx';

// Used both to edit an existing app (pocketid override or custom app) and to
// create a brand new custom app (pass app={source:'custom', isNew:true}).
export default function AppEditModal({ app, onClose, onSave, onDelete }) {
  const isCustom = app.source === 'custom';
  const [name, setName] = useState(app.name || '');
  const [description, setDescription] = useState(app.description || '');
  const [launchUrl, setLaunchUrl] = useState(app.launchUrl || '');
  const [iconPath, setIconPath] = useState(app.iconPath || '');
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setError(null);
    try {
      const { iconPath: uploaded } = await api.upload('/api/icons', file);
      setIconPath(uploaded);
    } catch (err) {
      setError('Icon upload failed: ' + err.message);
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    if (isCustom && !name.trim()) return setError('Name is required');
    if (isCustom && !launchUrl.trim()) return setError('URL is required');
    setSaving(true);
    setError(null);
    try {
      await onSave({
        name: isCustom ? name.trim() : name.trim() || null,
        description: description.trim() || null,
        launchUrl: isCustom ? launchUrl.trim() : undefined,
        iconPath: iconPath || null,
      });
    } catch (err) {
      setError(err.message);
      setSaving(false);
    }
  };

  const handleResetOverrides = async () => {
    setSaving(true);
    try {
      await onSave({ name: null, description: null, iconPath: null });
    } catch (err) {
      setError(err.message);
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="card w-full max-w-md p-5">
        <h2 className="mb-4 text-base font-bold">
          {app.isNew ? 'Add app' : isCustom ? 'Edit app' : 'Customise this app'}
        </h2>

        <div className="mb-4 flex items-center gap-3">
          <AppIcon app={{ ...app, iconPath }} size={44} />
          <label className="btn-ghost cursor-pointer text-xs">
            {uploading ? 'Uploading…' : 'Change icon'}
            <input type="file" accept="image/png,image/jpeg,image/svg+xml,image/webp" className="hidden" onChange={handleFile} disabled={uploading} />
          </label>
          {iconPath && (
            <button className="text-xs text-steel underline" onClick={() => setIconPath('')}>
              Remove
            </button>
          )}
        </div>

        <div className="mb-3">
          <label className="label">Name {!isCustom && <span className="normal-case font-normal">(blank = PocketID default)</span>}</label>
          <input className="input" value={name} onChange={(e) => setName(e.target.value)} placeholder={!isCustom ? app.name : ''} />
        </div>

        <div className="mb-3">
          <label className="label">Description {!isCustom && <span className="normal-case font-normal">(blank = default)</span>}</label>
          <textarea className="input" rows={2} value={description} onChange={(e) => setDescription(e.target.value)} />
        </div>

        {isCustom && (
          <div className="mb-3">
            <label className="label">URL</label>
            <input className="input" value={launchUrl} onChange={(e) => setLaunchUrl(e.target.value)} placeholder="https://…" />
          </div>
        )}

        {error && <p className="mb-3 text-xs text-ascred">{error}</p>}

        <div className="mt-4 flex items-center justify-between gap-2">
          <div>
            {!app.isNew && !isCustom && (
              <button className="text-xs text-steel underline" onClick={handleResetOverrides} disabled={saving}>
                Reset to default
              </button>
            )}
            {!app.isNew && isCustom && (
              <button className="text-xs text-ascred underline" onClick={onDelete} disabled={saving}>
                Delete app
              </button>
            )}
          </div>
          <div className="flex gap-2">
            <button className="btn-ghost" onClick={onClose} disabled={saving}>Cancel</button>
            <button className="btn-primary" onClick={handleSave} disabled={saving || uploading}>
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
